/**
 * Prototyper Facade (100% Shared)
 *
 * Wraps prototyper-related state and actions for WebContainer streaming:
 * publish, demo link metadata, and build-stream model selection.
 *
 * @module usePrototyperFacade
 * @category Facades/Shared
 */

import { useCallback, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  setIsFetchingPrototype,
  setPrototypeError,
  setCurrentPrototyper,
  updatePrototyperSlug,
  updatePrototyperDemoUrl,
  updatePrototyperPublication,
  clearPrototyper,
  clearPrototypeError,
  selectPrototyperState,
  selectPrototypeGenerationModel,
  setPrototypeGenerationModel,
  setStreamDemoLinkId,
} from '../../../store/slices/prototyperSlice';
import { prototyperService } from '../../../api/services/prototyper.service';
import type { PublishedPrototypeVersion } from '../../../types/prototyper.types';

export interface CurrentPrototyper {
  demo_url: string | null;
  prototyper_demo_link_id: number | null;
  slug: string | null;
  header: string | null;
  /** The version the main link serves; null when unpublished or not recorded. */
  publishedVersionId?: string | null;
  /** Every version published under the link, each at `/p/<slug>/v<number>`. */
  publishedVersions?: PublishedPrototypeVersion[];
}

export interface PrototyperFacade {
  prototypeError: string | null;
  currentPrototyper: CurrentPrototyper;
  cancelPrototype: () => void;
  /** Forget the loaded publish record (see the implementation). */
  clearPrototyperRecord: () => void;
  clearError: () => void;
  /** Show the prototype error state (with its Retry) for a failure detected outside a request. */
  reportError: (message: string) => void;
  updatePrototyperDemoLink: (id: number, slug?: string | null) => Promise<void>;
  /** Unpublish one version; the prototype stays published while any other version is. */
  unpublishVersion: (id: number, versionId: string) => Promise<void>;
  /** `llmInteractionId`: the turn whose version to publish (its prototype's link is written). */
  publishToDemo: (
    projectId: string,
    conversationId: string,
    slug: string,
    snapshotId?: string,
    llmInteractionId?: string,
  ) => Promise<{ slug: string | null; previewUrl: string | null; demoLinkId: number | null }>;
  fetchPrototyperById: (id: string | number) => Promise<void>;
  setStreamDemoLinkId: (id: number) => void;
  prototypeGenerationModel: { modelProvider: string; modelChoice: string };
  setPrototypeGenerationModel: (model: { modelProvider: string; modelChoice: string }) => void;
}

export function usePrototyperFacade(
  _projectId?: string | null,
  activeConversationId?: string | null,
): PrototyperFacade {
  const dispatch = useAppDispatch();
  const prototyperState = useAppSelector(selectPrototyperState);
  const prototypeGenerationModel = useAppSelector(selectPrototypeGenerationModel);

  /**
   * Live pointer to the conversation currently on screen (plain assignment, not a ref
   * synced via effect, so it's already current by the time an in-flight request below
   * resolves). `currentPrototyper` in Redux is a single flat object with no per-chat key,
   * so without this guard a publish/fetch/update-demo-link request started in chat A that
   * resolves after the user has switched to chat B would still overwrite B's prototype
   * state with A's stale result.
   */
  const activeConversationIdRef = useRef(activeConversationId);
  activeConversationIdRef.current = activeConversationId;

  const setGenerationModel = useCallback(
    (model: { modelProvider: string; modelChoice: string }) => {
      dispatch(setPrototypeGenerationModel(model));
    },
    [dispatch],
  );

  const safePrototyperState = prototyperState || {
    currentPrototyper: {
      demo_url: null,
      prototyper_demo_link_id: null,
      slug: null,
      header: null,
    },
    prototypeError: null,
  };

  const cancelPrototype = useCallback(() => {
    dispatch(clearPrototyper());
  }, [dispatch]);

  /**
   * Forget the loaded publish record. It is one slot in the store, so whatever was last
   * published stays in it until something says otherwise — which showed a brand-new prototype
   * as already published, under the previous one's link. Callers clear it whenever the prototype
   * on screen changes, and load the new one's record if it has one.
   */
  const clearPrototyperRecord = useCallback(() => {
    dispatch(clearPrototyper());
  }, [dispatch]);

  const clearError = useCallback(() => {
    dispatch(clearPrototypeError());
  }, [dispatch]);

  const reportError = useCallback(
    (message: string) => {
      dispatch(setPrototypeError(message));
    },
    [dispatch],
  );

  const updatePrototyperDemoLink = useCallback(
    async (id: number, slug?: string | null) => {
      const requestConversationId = activeConversationIdRef.current;
      try {
        await prototyperService.updatePrototyperDemoLink({ id, slug });
        // Skip if the user has since switched to a different conversation — this
        // dispatch would otherwise overwrite that conversation's prototype state.
        if (activeConversationIdRef.current !== requestConversationId) return;
        if (slug !== undefined) {
          dispatch(updatePrototyperSlug(slug));
        }
        // Unpublish: the backend drops every version link with the main one.
        if (slug === null) {
          dispatch(updatePrototyperPublication({ publishedVersionId: null, publishedVersions: [] }));
        }
      } catch (error) {
        console.error('Error updating prototyper demo link:', error);
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to update prototyper demo link';
        if (activeConversationIdRef.current === requestConversationId) {
          dispatch(setPrototypeError(errorMessage));
        }
        throw error;
      }
    },
    [dispatch],
  );

  const unpublishVersion = useCallback(
    async (id: number, versionId: string) => {
      const requestConversationId = activeConversationIdRef.current;
      const response = await prototyperService.unpublishPrototypeVersion(id, versionId);
      if (activeConversationIdRef.current !== requestConversationId) return;
      dispatch(updatePrototyperSlug(response.slug ?? null));
      dispatch(
        updatePrototyperPublication({
          publishedVersionId: response.publishedVersionId ?? null,
          publishedVersions: response.publishedVersions ?? [],
        }),
      );
    },
    [dispatch],
  );

  const fetchPrototyperById = useCallback(
    async (id: string | number) => {
      const requestConversationId = activeConversationIdRef.current;
      try {
        dispatch(setIsFetchingPrototype(true));
        const response = await prototyperService.getPrototyperById(id);
        // Skip if the user has since switched to a different conversation — this
        // dispatch would otherwise overwrite that conversation's prototype state.
        if (activeConversationIdRef.current !== requestConversationId) return;
        // `response.id`/`response.prototyper_demo_link_id` come back as strings (bigint
        // column serialized to JSON) — coerce to a number so this matches the `number |
        // null` type `CurrentPrototyper.prototyper_demo_link_id` declares. Dispatching the
        // raw string here previously made every subsequent `numId !== prototyperDemoLinkId`
        // comparison against a fetched numeric id permanently true (123 !== "123"), which
        // sent callers of this facade (e.g. the snapshot-restore backfill effect in
        // ConversationPage) into an infinite refetch loop.
        const rawPrototyperId = response.id ?? response.prototyper_demo_link_id;
        const prototyperId = Number(rawPrototyperId);
        dispatch(
          setCurrentPrototyper({
            demo_url: response.demo_url || response.demoUrl || null,
            prototyper_demo_link_id: Number.isFinite(prototyperId) ? prototyperId : null,
            slug: response.slug || null,
            header: response.header || null,
            publishedVersionId: response.publishedVersionId ?? null,
            publishedVersions: response.publishedVersions ?? [],
          }),
        );
      } catch (error) {
        console.error('Error fetching prototyper:', error);
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to fetch prototyper';
        if (activeConversationIdRef.current === requestConversationId) {
          dispatch(setPrototypeError(errorMessage));
        }
        throw error;
      } finally {
        // Always clear the loading flag, even if stale — it's a plain boolean spinner,
        // not per-conversation state, and skipping this would leave it stuck `true`.
        dispatch(setIsFetchingPrototype(false));
      }
    },
    [dispatch],
  );

  const setStreamDemoLinkIdCb = useCallback(
    (id: number) => {
      dispatch(setStreamDemoLinkId(id));
    },
    [dispatch],
  );

  const publishToDemo = useCallback(
    async (
      projectId: string,
      conversationId: string,
      slug: string,
      snapshotId?: string,
      llmInteractionId?: string,
    ) => {
      try {
        const response = await prototyperService.publishPrototype(projectId, conversationId, {
          slug,
          snapshotId,
          llmInteractionId,
        });
        // Skip if the user has since switched away from `conversationId` — this dispatch
        // would otherwise overwrite the now-active conversation's prototype state.
        if (activeConversationIdRef.current !== conversationId) {
          return { slug: null, previewUrl: null, demoLinkId: null };
        }
        dispatch(updatePrototyperSlug(response.slug));
        dispatch(updatePrototyperDemoUrl(response.previewUrl));
        dispatch(
          updatePrototyperPublication({
            publishedVersionId: response.publishedVersionId ?? null,
            publishedVersions: response.publishedVersions ?? [],
          }),
        );
        // The server always resolves (or auto-provisions) a `prototyper_demo_links` row for
        // this conversation before deploying — populate it here rather than waiting on the
        // live `demoLinkCreated` SSE event, which a resumed/restored build never replays.
        // Without this, `currentPrototypeId` stays null after a publish in this same session,
        // and Unpublish (which still goes through the id-keyed PATCH endpoint) fails with
        // "No prototype ID available" even though the prototype just published successfully.
        const demoLinkId = Number(response.prototyperDemoLinkId);
        if (Number.isFinite(demoLinkId) && demoLinkId > 0) {
          dispatch(setStreamDemoLinkId(demoLinkId));
        }
        // Returned as well as dispatched: the turn's message has to learn the new link too, or
        // anything that rebuilds this state from the conversation (a prototype switch, a
        // restore) goes back to showing the prototype as unpublished until a page reload.
        return {
          slug: response.slug ?? null,
          previewUrl: response.previewUrl ?? null,
          demoLinkId: Number.isFinite(demoLinkId) && demoLinkId > 0 ? demoLinkId : null,
        };
      } catch (error) {
        console.error('Error publishing prototype:', error);
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to publish prototype';
        if (activeConversationIdRef.current === conversationId) {
          dispatch(setPrototypeError(errorMessage));
        }
        throw error;
      }
    },
    [dispatch],
  );

  return {
    prototypeError: safePrototyperState.prototypeError,
    currentPrototyper: safePrototyperState.currentPrototyper,
    cancelPrototype,
    clearPrototyperRecord,
    clearError,
    reportError,
    updatePrototyperDemoLink,
    unpublishVersion,
    fetchPrototyperById,
    publishToDemo,
    setStreamDemoLinkId: setStreamDemoLinkIdCb,
    prototypeGenerationModel,
    setPrototypeGenerationModel: setGenerationModel,
  };
}
